import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { useQuery } from "@supabase-cache-helpers/postgrest-react-query";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Tables } from "../../utils/database.types";
import useSupabaseBrowser from "../hooks/use-supabase-browser";
import { getVenues } from "../api/get-venues";
import Pagination from "./Pagination";
import ButtonAction from "./ButtonAction";

library.add(faCheck);

const VenuesTable = () => {
  const supabase = useSupabaseBrowser();
  const [sortingColumn, setSortingColumn] = useState<string | null>(null);
  const [isDescending, setIsDescending] = useState<boolean>(false);
  const [pageIndex, setPageIndex] = useState(0);
  const pageSize = 10;
  const {
    data: venues = [],
    isLoading,
    isError,
    refetch,
  } = useQuery(
    getVenues(supabase, pageIndex, pageSize, sortingColumn, isDescending)
  );
  const columnHelper = createColumnHelper<Tables<"venues">>();

  const handleSort = (columnId: string) => {
    if (sortingColumn === columnId) {
      setIsDescending(!isDescending);
    } else {
      setSortingColumn(columnId);
      setIsDescending(false);
    }
    setPageIndex(0);
  };

  const columns = [
    columnHelper.accessor("name", {
      header: () => <span onClick={() => handleSort("name")}>Name</span>,
      cell: (info) => info.getValue(),
      footer: (info) => info.column.id,
    }),
    columnHelper.accessor("timezone", {
      header: () => (
        <span onClick={() => handleSort("timezone")}>Timezone</span>
      ),
      cell: (info) => info.getValue(),
      footer: (info) => info.column.id,
    }),
    columnHelper.accessor("phone_number", {
      header: () => (
        <span onClick={() => handleSort("phone_number")}>Phone number</span>
      ),
      cell: (info) => info.getValue(),
      footer: (info) => info.column.id,
    }),
    columnHelper.accessor("estimated_waiting_time", {
      header: () => (
        <span onClick={() => handleSort("estimated_waiting_time")}>
          Estimated waiting time
        </span>
      ),
      cell: (info) => info.getValue(),
      footer: (info) => info.column.id,
    }),
    columnHelper.accessor("payment", {
      header: "Payments",
      cell: (info) =>
        info.getValue() ? (
          <FontAwesomeIcon size="2x" icon={["fas", "check"]} />
        ) : null,
      footer: (info) => info.column.id,
    }),
    columnHelper.accessor("tips", {
      header: "Tips",
      footer: (info) => info.column.id,
      cell: (info) =>
        info.getValue() ? (
          <FontAwesomeIcon size="2x" icon={["fas", "check"]} />
        ) : null,
    }),
    columnHelper.display({
      id: "actions",
      cell: ({ row }) => {
        return (
          <div className="join">
            <Link
              className="btn btn-xs btn-warning join-item"
              to={`/venues/${row.original.id}/edit`}
            >
              Edit
            </Link>
            <ButtonAction
              subject="venues"
              text={"Archive"}
              mutationData={{
                archived: true,
                id: row.original.id,
              }}
              onSuccess={refetch}
              confirmationText={`Are you sure you want to archive ${row.original.name}? This action cannot be undone.`}
            />
          </div>
        );
      },
    }),
  ];

  const table = useReactTable({
    data: venues ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      pagination: { pageIndex, pageSize },
    },
    manualPagination: true,
  });

  if (isLoading) {
    return <span className="loading loading-dots loading-lg"></span>;
  }

  if (isError || !venues) {
    return <div>Error</div>;
  }

  return (
    <section className="card overflow-hidden bg-base-100 shadow-sm">
      <div className="card-body">
        <div className="flex flex-row md:flex-row justify-end">
          <Link to="/venues/add" className="btn btn-primary mt-2 md:mt-0">
            Add ($50/month)
          </Link>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="table table-zebra w-full">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <th key={header.id} colSpan={header.colSpan}>
                      {header.isPlaceholder ? null : (
                        <div>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => {
              const venueId = row.original.id;
              return (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex justify-center p-4">
        <Pagination
          pageIndex={pageIndex}
          setPageIndex={setPageIndex}
          isPreviousPageAvailable={pageIndex > 0}
          isNextPageAvailable={venues.length === pageSize}
        />
      </div>
    </section>
  );
};

export default VenuesTable;
